import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import Sticky from 'react-stickynode';
import { DrawerProvider } from 'common/src/contexts/DrawerContext';
import { saasModernTheme } from 'common/src/theme/saasModern';
import { ResetCSS } from 'common/src/assets/css/style';
import { GlobalStyle, ContentWrapper } from '../containers/sassModern.style';

import BannerSection from '../containers/Banner';
import Navbar from '../containers/Navbar';
import WorkingProcessSection from '../containers/WorkingProcess';
import TextBoxSection from '../containers/TextBox';
import InfoSection from '../containers/Info';
import ComparisonTable from '../containers/ComparisonTable';
import Footer from '../containers/Footer';
import SEO from '../components/seo';
import FaqSection from '../containers/Faq';
import { useTranslation } from 'react-i18next';

const MozillaFirefoxPasswordManager = ({ title, description, pageContext }) => {
  const { t } = useTranslation();

  const firefoxFeatures = [
    {
      title: 'Firefox Privacy Protection',
      alt: 'Firefox Privacy Protection',
      description:
        "Zero-tracking password management that aligns with Firefox's privacy philosophy. No analytics, no data collection, complete privacy for all your credentials.",
    },
    {
      title: 'Firefox Add-ons Available',
      alt: 'Firefox Add-ons Available',
      description:
        'Distributed through the Firefox Add-ons store for secure installation. Built to meet Firefox extension standards for security, privacy, and compatibility.',
    },
    {
      title: 'Firefox Container Support',
      alt: 'Firefox Container Support',
      description:
        'Works seamlessly with Firefox Multi-Account Containers. Maintain separate password vaults for different contexts—work, personal, shopping, and more.',
    },
    {
      title: 'Cross-Platform Firefox Sync',
      alt: 'Cross-Platform Firefox Sync',
      description:
        'Access your passwords on Firefox desktop, Firefox mobile, and Firefox Developer Edition. Synchronized encryption across Windows, Mac, Linux, Android, and iOS.',
    },
    {
      title: 'Firefox Password Import',
      alt: 'Firefox Password Import',
      description:
        'Easily migrate your existing Firefox saved passwords to Psono. Simple CSV import process transfers all your credentials with complete data integrity.',
    },
    {
      title: 'Open Source Transparency',
      alt: 'Open Source Transparency',
      description:
        'Firefox users value open source—Psono delivers. Fully auditable code base reviewed by security experts worldwide. No hidden functionality or backdoors.',
    },
  ];

  const firefoxFaqData = [
    {
      title: 'Does Psono work well with Firefox?',
      description:
        'Psono provides Firefox users with enterprise-grade password management without cost barriers. Built entirely on open-source code, the extension allows security professionals to verify its security implementation. Organizations requiring complete data control can deploy self-hosted instances. Combining NaCl encryption with multi-device synchronization and comprehensive free-tier access, Psono presents a viable alternative to subscription-based password managers for Firefox users.',
      expend: false,
    },
    {
      title: 'How do I add Psono to Firefox?',
      description:
        'Begin by registering at psono.pw and verifying your email. Open Firefox and access the Add-ons marketplace to locate and install Psono. After installation completes, find the Psono icon in your toolbar and authenticate using your account details. Once active, Psono begins capturing credentials automatically while synchronizing your vault across all connected devices.',
      expend: false,
    },
    {
      title: "What's the top free password manager for Firefox?",
      description:
        "Firefox users benefit from Psono's zero-cost password management solution. The extension leverages transparent, auditable open-source code that security professionals can review. Installation through Firefox Add-ons takes moments, after which the extension operates automatically—storing credentials, generating secure passwords, and maintaining vault synchronization across your entire device ecosystem.",
      expend: false,
    },
    {
      title: 'Can I extract my passwords from Psono?',
      description:
        'Psono supports complete data portability via its export functionality. Within the web application, access the "Other" menu and select "Export". Choose your datastore and preferred format (JSON or CSV). After confirmation, download a file containing all accessible vault items, ensuring you retain full ownership of your password data.',
      expend: false,
    },
    {
      title: 'How do I transfer Firefox passwords to Psono?',
      description:
        'Start by exporting Firefox credentials: Navigate to Settings > Privacy & Security > Logins and Passwords, select the menu icon, and choose "Export Logins" to generate a CSV file. In Psono\'s web interface, open "Other" > "Import", choose "Chrome CSV" as the format (compatible with Firefox exports), upload your CSV file, and confirm. Your Firefox passwords will migrate into Psono\'s encrypted vault with full cross-device sync capability.',
      expend: false,
    },
  ];

  const comparisonFeatures = [
    {
      title: 'Advanced Encryption',
      description:
        'Protect passwords with NaCl cryptography. Vault data is encrypted client-side before it is sent to the server.',
      psonoValue: true,
      firefoxValue: '-',
    },
    {
      title: 'Open Source & Auditable',
      description:
        'Publicly available source code allows security experts worldwide to verify security claims. Regular third-party audits ensure continued protection.',
      psonoValue: true,
      firefoxValue: '-',
    },
    {
      title: 'Advanced Two-Factor Authentication',
      description:
        'Secure your vault with TOTP authenticators, hardware security keys, or FIDO2 WebAuthn credentials for enhanced protection.',
      psonoValue: true,
      firefoxValue: '-',
    },
    {
      title: 'Password Generator',
      description:
        'Create long, complex, and unique passwords for every site with customizable options for maximum security.',
      psonoValue: true,
      firefoxValue: '-',
    },
    {
      title: 'Self-Hosting Option',
      description:
        'Deploy on your own infrastructure for complete control over your data and compliance with regulatory requirements.',
      psonoValue: true,
      firefoxValue: '-',
    },
    {
      title: 'Cross-Platform Sync',
      description:
        'Access your passwords on any browser, mobile device, or desktop OS with automatic synchronization across all devices.',
      psonoValue: true,
      firefoxValue: 'Limited',
    },
  ];

  return (
    <ThemeProvider theme={saasModernTheme}>
      <Fragment>
        <SEO
          title={t('Best Free Password Manager Mozilla Firefox | Psono')}
          lang={pageContext.lang}
          titleTemplate={`%s`}
          description={t(
            'Secure password manager extension for Firefox. Store unlimited passwords, sync across devices, and protect your data with military-grade encryption. Free open-source solution.'
          )}
          keywords={[
            'password manager firefox',
            'firefox password extension',
            'password manager mozilla',
            'best free password manager for firefox',
            'firefox password manager',
            'mozilla firefox password manager',
            'firefox extension password manager',
            'secure password manager firefox',
            'firefox password vault',
            'browser password manager firefox',
          ]}
        />

        <ResetCSS />
        <GlobalStyle />

        <ContentWrapper>
          <Sticky top={0} innerZ={9999} activeClass="sticky-nav-active">
            <DrawerProvider>
              <Navbar pageContext={pageContext} />
            </DrawerProvider>
          </Sticky>
          <BannerSection
            pageContext={pageContext}
            topText={t('Open-source password security for Firefox')}
            titleText={t('Mozilla Firefox Password Manager')}
            descriptionText={t(
              'Best free password manager for Firefox. Secure Mozilla Firefox password extension with unlimited storage, privacy-first encryption, and seamless sync.'
            )}
            primeLink={
              'https://addons.mozilla.org/en-US/firefox/addon/psono-pw-password-manager/'
            }
            primeText={t('INSTALL PSONO FOR FIREFOX NOW')}
            downloadLink={`${pageContext.langPathPrefix}/download`}
            downloadText={t('VIEW ALL DOWNLOAD OPTIONS')}
          />
          <TextBoxSection
            description={t(
              'Secure password management built for Firefox users'
            )}
            text={t(
              "Psono delivers open-source password management designed to complement Firefox's privacy-focused philosophy. Access unlimited credential storage, cross-platform synchronization, and premium security features—all available in a permanently free tier that respects your data sovereignty."
            )}
          />
          <ComparisonTable
            features={comparisonFeatures.map((f) => ({
              ...f,
              chromeValue: f.firefoxValue,
            }))}
            browserName="Firefox"
            title={t(
              'Top reasons to choose Psono over Mozilla Firefox native password manager'
            )}
          />
          <TextBoxSection
            description={t(
              'Essential password protection for Firefox browsers'
            )}
            text={t(
              'Firefox updates and OS security patches protect against browser vulnerabilities, but password security requires additional layers of protection. The critical risks include credential reuse across services, predictable password patterns, and insufficient authentication mechanisms. Psono mitigates these risks through cryptographic password generation for each account, local NaCl encryption before synchronization, and optional multi-factor authentication for vault access. The Firefox extension provides seamless toolbar integration for quick credential access while maintaining encryption throughout the entire storage and sync process.'
            )}
          />
          <WorkingProcessSection
            title={t('Why choose Psono as your Firefox password manager?')}
            description={t(
              'Key features that make Psono stand out for Firefox users'
            )}
            buttonUrl="/features-for-users"
            items={firefoxFeatures}
            pageContext={pageContext}
          />
          <TextBoxSection
            description={t('Quick setup guide for Firefox')}
            text={t(
              'Getting Psono running in Firefox requires minimal effort: Register your Psono account and complete email verification. Navigate to Firefox Add-ons to install the extension—the Psono icon will appear in your toolbar immediately. Authenticate through the extension interface with your credentials. Psono then activates, offering to securely store login information as you browse. Returning to saved sites becomes effortless with one-click autofill from the toolbar. Your encrypted vault maintains real-time synchronization across Windows, Mac, Linux, iOS, and Android platforms.'
            )}
          />
          <InfoSection
            pageContext={pageContext}
            titleContent={t('Firefox Extension Security')}
            descriptionContent={t(
              "The Psono Firefox extension implements NaCl cryptographic standards for local encryption, securing your passwords before any network transmission occurs. Our publicly accessible source code receives ongoing scrutiny from the international security community. Firefox users particularly benefit from Psono's zero-tracking policy and privacy-first architecture. Whether safeguarding personal credentials or managing enterprise access systems, Psono integrates battle-tested encryption directly into your Firefox workflow."
            )}
          />
          <FaqSection data={firefoxFaqData} />
          <Footer pageContext={pageContext} />
        </ContentWrapper>
      </Fragment>
    </ThemeProvider>
  );
};

MozillaFirefoxPasswordManager.propTypes = {
  title: PropTypes.object,
  description: PropTypes.object,
};

MozillaFirefoxPasswordManager.defaultProps = {
  title: {
    fontSize: ['24px', '32px', '40px', '42px', '46px'],
    fontWeight: '700',
    color: '#fff',
    letterSpacing: '-0.025em',
    mb: ['20px', '25px', '25px', '25px', '25px'],
    lineHeight: '1.2',
    textAlign: 'center',
  },
  description: {
    fontSize: ['15px', '16px', '16px', '16px', '16px'],
    color: '#fff',
    lineHeight: '1.75',
    mb: '0',
    textAlign: 'center',
  },
};

export default MozillaFirefoxPasswordManager;
